import { Refreshable } from '../../utils/refreshable/IRefreshable';
import {
  IEntitlementsService,
  EntitlementsInputType
} from './IEntitlementsService';
import { SetServiceDependencies } from '../../infra/startup/types';
import { PromiseHelper } from '../../utils/PromiseHelper';
import EventNames from '../../config/eventNames';
import getProgramInfo from '../../clients/stratus/entitlements/getProgramInfo';
import getUserWithTenantDetails from '../../clients/stratus/usersMgt/getUserWithTenantDetails';
import {
  ProgramInfoMapPropertyType,
  ProgramInfoResponseType
} from '../../clients/stratus/entitlements/getProgramInfo/types';
import { UserContextEnum } from '../../interface/types';
import {
  OfferingAndEntitlementType,
  OfferingAndEntitlementOptionsType,
  RefreshType,
  AuthProviderV2Type
} from '../../interface/v1/entitlements/types';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import userContextEnumToAuthContextEnum from '../authTokenService/utils/userContextEnumToAuthContextEnum';
import { IAuthTokenService } from '../authTokenService';
import { EntitlementRepository } from '../entitlements/EntitlementRepository';
import IEventService from '../eventService/IEventService';
import IAuthProviderService from '../authProviderService/IAuthProviderService';
import IApplicationService from '../applicationService/IApplicationService';
import { AuthPluginFacade } from '../JWeb/facades/AuthPluginFacade';
import AuthenticationProviderEnum from '../../config/authenticationProviderEnum';
import getAuthenticationProviderByToken from '../../utils/tokenUtils/getAuthenticationProviderByToken';
import getResourceIds from '../../utils/tokenUtils/getResourceIds';
export class EntitlementsService
  extends Refreshable
  implements IEntitlementsService
{
  private defaultKey = 'default';
  private initializedUserContexts: Map<
    UserContextEnum | typeof this.defaultKey,
    boolean
  > = new Map();
  private enabled = false;
  private promiseHelper: PromiseHelper;

  // Dependencies
  private authTokenService: IAuthTokenService;
  private entitlementRepository: EntitlementRepository;
  private eventService: IEventService;
  private authProvider: IAuthProviderService;
  private applicationService: IApplicationService;

  constructor(config: EntitlementsInputType) {
    super();
    const { enabled } = config || {};
    this.enabled = enabled;
    this.promiseHelper = new PromiseHelper();

    this.setRefreshMethods({
      init: this.init.bind(this),
      refresh: this.refresh.bind(this)
    });
  }

  setDependencies({ services, repositories }: SetServiceDependencies): void {
    this.authTokenService = services.authTokenService;
    this.entitlementRepository = repositories.entitlementRepository;
    this.eventService = services.eventService;
    this.authProvider = services.authProviderService;
    this.applicationService = services.applicationService;
    this._markAsReady();
  }

  private _isDifferentFromCache(
    newProgramInfo: ProgramInfoResponseType,
    authContextSuffix: string
  ): boolean {
    try {
      const cachedProgramInfo: ProgramInfoResponseType =
        this.entitlementRepository.findOne(authContextSuffix);

      const newProgramInfoString = JSON.stringify(newProgramInfo);
      const cachedProgramInfoString = JSON.stringify(cachedProgramInfo);

      return newProgramInfoString !== cachedProgramInfoString;
    } catch (err) {
      internalLogger?.error?.(err);
      return true;
    }
  }

  private _getAuthProviderByUserContextEnum(
    userContextTypeString: string
  ): AuthProviderV2Type {
    const userContext = userContextTypeString
      ? UserContextEnum[userContextTypeString]
      : null;
    return this.authProvider.createAuthProviderByUserContextEnum(
      userContext
    ) as unknown as AuthProviderV2Type;
  }

  private _getAuthContextSuffix(
    options?: OfferingAndEntitlementOptionsType
  ): string {
    const authContext = options?.userContext
      ? userContextEnumToAuthContextEnum(options.userContext)
      : this.authTokenService.getCurrentContext();
    return this.authTokenService.getSuffix(authContext);
  }

  private async refreshAction(
    options?: OfferingAndEntitlementOptionsType
  ): Promise<void> {
    // In fallback state, do not attempt to fetch entitlements since we cannot get fresh tokens
    if (AuthPluginFacade.IS_COMMONS_IN_FALLBACK_STATE) {
      console.log(
        '@clientos/commons-norhtbound-api - EntitlementsService - refreshAction - Skipping entitlements refresh during fallback state'
      );
      return;
    }

    const _authProvider = this._getAuthProviderByUserContextEnum(
      options?.userContext
    );

    const accessToken = await _authProvider.getAccessToken();
    const { tenantResourceId, userResourceId } = await this._getResourceIds(
      _authProvider,
      accessToken
    );

    const programInfoDetailsResponse = await getProgramInfo({
      authProvider: _authProvider,
      stack: this.applicationService.getAuthStack(),
      tenantResourceId,
      userResourceId
    });

    const newProgramInfo =
      programInfoDetailsResponse?.data || ({} as ProgramInfoResponseType);

    const authContextSuffix = this._getAuthContextSuffix(options);

    if (this._isDifferentFromCache(newProgramInfo, authContextSuffix)) {
      this.entitlementRepository.save(authContextSuffix, newProgramInfo);
      this.eventService.publish(
        EventNames.shellEntitlementChangedEventName,
        undefined
      );
    }
  }

  async refresh(
    refreshOption?: RefreshType,
    options?: OfferingAndEntitlementOptionsType
  ): Promise<void> {
    const promiseKey = options?.userContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(promiseKey);
    if (this.isEnabled() && promise === undefined) {
      promise = this.refreshAction(options);
      this.promiseHelper.setPendingPromise(promiseKey, promise);
    }
    await promise;
  }

  async init(options?: OfferingAndEntitlementOptionsType): Promise<void> {
    const contextKey = options?.userContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(contextKey);
    const isInitialized = this.initializedUserContexts.get(contextKey);

    if (!isInitialized) {
      this.initializedUserContexts.set(contextKey, true);
      if (promise === undefined) {
        promise = this.refresh({ reloadContent: false }, options);
        this.promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    await promise;
  }

  getFlatMap<key extends keyof ProgramInfoMapPropertyType>(
    propertyName: key,
    authContextSuffix: string
  ): ProgramInfoMapPropertyType[key] {
    const programInfo: ProgramInfoResponseType =
      this.entitlementRepository.findOne(authContextSuffix);

    let flatmap: ProgramInfoMapPropertyType[typeof propertyName] = [];

    if (typeof programInfo?.programInfoMap === 'object') {
      Object.values(programInfo?.programInfoMap).forEach((value) => {
        if (value && value[propertyName]) {
          flatmap = [
            ...flatmap,
            ...value[propertyName]
          ] as ProgramInfoMapPropertyType[typeof propertyName];
        }
      });
    }
    return flatmap.reverse() as ProgramInfoMapPropertyType[typeof propertyName];
  }

  async checkEntitlements(
    entitlements: OfferingAndEntitlementType[],
    options?: OfferingAndEntitlementOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    await this.init(options);

    const entitlementAndOfferingKey = this._getAuthContextSuffix(options);

    const flatMapEntitlements = this.getFlatMap(
      'entitlementList',
      entitlementAndOfferingKey
    );
    const testEntitlements = entitlements?.every?.(
      (argumentEntitlement) =>
        !!flatMapEntitlements?.find?.(
          (cachedEntitlement) =>
            cachedEntitlement?.state == argumentEntitlement?.state &&
            cachedEntitlement?.serviceId == argumentEntitlement?.serviceId
        )
    );
    return !!testEntitlements;
  }

  async checkOfferings(
    offerings: OfferingAndEntitlementType[],
    options?: OfferingAndEntitlementOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    await this.init(options);

    const entitlementAndOfferingKey = this._getAuthContextSuffix(options);

    const flatMapOfferings = this.getFlatMap(
      'subscriptionOfferingList',
      entitlementAndOfferingKey
    );
    const testOffering = offerings?.every?.(
      (argumentOffering) =>
        !!flatMapOfferings?.find?.(
          (cachedOffering) =>
            cachedOffering?.state == argumentOffering?.state &&
            cachedOffering?.serviceId == argumentOffering?.serviceId
        )
    );
    return !!testOffering;
  }

  async getPlanInfo(options?: OfferingAndEntitlementOptionsType): Promise<any> {
    if (!this.isEnabled()) return undefined;

    await this.init(options);

    const authContextSuffix = this._getAuthContextSuffix(options);

    const programInfo: ProgramInfoResponseType =
      this.entitlementRepository.findOne(authContextSuffix);
    return programInfo?.planInfo;
  }

  async getOffering(
    serviceId: string,
    options?: OfferingAndEntitlementOptionsType
  ): Promise<any> {
    if (!this.isEnabled()) return undefined;

    await this.init(options);

    const entitlementAndOfferingKey = this._getAuthContextSuffix(options);

    const flatOfferingMap = this.getFlatMap(
      'subscriptionOfferingList',
      entitlementAndOfferingKey
    );
    return flatOfferingMap?.find?.(
      (offering) => offering?.serviceId === serviceId
    );
  }

  async getEntitlement(
    serviceId: string,
    options?: OfferingAndEntitlementOptionsType
  ): Promise<any> {
    if (!this.isEnabled()) return undefined;

    await this.init(options);

    const entitlementAndOfferingKey = this._getAuthContextSuffix(options);

    const flatEntitlememtsMap = this.getFlatMap(
      'entitlementList',
      entitlementAndOfferingKey
    );
    return flatEntitlememtsMap?.find?.(
      (entitlement) => entitlement?.serviceId === serviceId
    );
  }

  isEnabled(): boolean {
    if (!this.enabled) {
      console.warn('Entitlements: disabled');
      return false;
    }
    return true;
  }

  private async _getResourceIds(
    authProvider: AuthProviderV2Type,
    accessToken: string
  ): Promise<{
    tenantResourceId: string;
    userResourceId: string;
  }> {
    const tokenProvider = getAuthenticationProviderByToken(accessToken);

    if (tokenProvider === AuthenticationProviderEnum.coptor) {
      const { tenantResourceId, userResourceId } = getResourceIds(accessToken);
      return {
        tenantResourceId,
        userResourceId
      };
    }

    const userAndTenantDetailsResponse = await getUserWithTenantDetails({
      authProvider,
      stack: this.applicationService.getAuthStack()
    });

    return {
      tenantResourceId:
        userAndTenantDetailsResponse?.data?.userTenantDetail?.tenantResourceId,
      userResourceId: userAndTenantDetailsResponse?.data?.user?.resourceId
    };
  }
}
